import type { ReactNode } from "react";
import { Icon } from "@focus/focusUI";

interface FullscreenPanelCardProps {
  children: ReactNode;
  fullscreenLabel: string;
  isFullscreen: boolean;
  onToggleFullscreen: () => void;
  title: string;
  badge?: ReactNode;
  collapsedClassName?: string;
  controls?: ReactNode;
  subtitle?: string;
  titleClassName?: string;
}

const fullscreenCardClassName =
  "fixed inset-0 z-[9999] flex h-screen min-h-0 w-screen flex-col overflow-hidden bg-white shadow-2xl";

const defaultCollapsedCardClassName =
  "flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm";

export const FullscreenPanelCard = ({
  badge,
  children,
  collapsedClassName = defaultCollapsedCardClassName,
  controls,
  fullscreenLabel,
  isFullscreen,
  onToggleFullscreen,
  subtitle,
  title,
  titleClassName = "min-w-0 flex-1",
}: FullscreenPanelCardProps) => (
  <section
    className={isFullscreen ? fullscreenCardClassName : collapsedClassName}
  >
    <header className="shrink-0 border-b border-slate-200 bg-white px-4 py-3">
      <div className="flex min-w-0 flex-wrap items-center gap-3">
        <div className={titleClassName}>
          <h2 className="text-base font-semibold text-slate-950">{title}</h2>
          {subtitle && (
            <p className="mt-1 text-sm font-medium text-slate-500">
              {subtitle}
            </p>
          )}
        </div>

        {controls}

        {badge && (
          <span className="shrink-0 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold uppercase text-slate-600">
            {badge}
          </span>
        )}

        <button
          aria-label={
            isFullscreen
              ? `Exit ${fullscreenLabel} fullscreen`
              : `${fullscreenLabel} fullscreen`
          }
          aria-pressed={isFullscreen}
          className="inline-flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-50 hover:text-slate-950"
          onClick={onToggleFullscreen}
          title={
            isFullscreen
              ? `Exit ${fullscreenLabel} fullscreen`
              : `${fullscreenLabel} fullscreen`
          }
          type="button"
        >
          <Icon icon={isFullscreen ? "minimize" : "fullscreen"} size={14} />
        </button>
      </div>
    </header>

    <div className="min-h-0 flex-1 bg-slate-50/40 p-3">
      <div className="h-full min-h-0 overflow-hidden rounded-xl border border-slate-200 bg-white">
        {children}
      </div>
    </div>
  </section>
);
